import { useState, useEffect, useMemo, useRef } from 'react';
import { useElevateNotifCount } from '../hooks/useElevate';
import { createPortal } from 'react-dom';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { api } from '../lib/api';
import { Avatar, Logo } from './Logo';
import { PendingSignupsModal } from './PendingSignupsModal';
import { PushToggle } from './PushToggle';
import { MobileBottomNav } from './MobileBottomNav';
import { useIsMobile } from '../hooks/useIsMobile';
import {
  Home, CheckSquare, Target, Users, Calendar, FileText, MessageCircle, Folder,
  LogOut, LayoutGrid, Bell, Menu, X, AlertTriangle, Archive, Lightbulb, Check,
  Sparkles, ChevronDown, TrendingUp, SettingsIcon,
} from './icons';

// Iter47k — Sidebar reorganisé selon Rayan (msg 559) :
//   Principal          → Overview, Messagerie
//   Planning           → Tâches, Calendrier, Roadmap client
//   Espace de travail  → Notes, Bibliothèque, ASA Script, Projets
//   Paramètres         → Équipe, Archives
// Iter 62cu — 6 items du menu "cachables" (toggle par user dans /reglages).
// Les 2 items Elevate sont cachés PAR DÉFAUT (Rayan msg 2026-02-20 : "les deux
// tu peux les supprimer"). Les 4 autres visibles par défaut.
export const SIDEBAR_HIDEABLE = [
  { key: 'sales-elevate',   label: 'Sales Elevate',   defaultHidden: true  },
  { key: 'elevate-school',  label: 'Elevate School',  defaultHidden: true  },
  { key: 'client-roadmap',  label: 'Roadmap client',  defaultHidden: false },
  { key: 'objectives',      label: 'Objectifs',       defaultHidden: false },
  { key: 'clients',         label: 'Clients',         defaultHidden: false },
  { key: 'asa-script',      label: 'ASA Script',      defaultHidden: false },
];
const SIDEBAR_HIDEABLE_DEFAULTS = SIDEBAR_HIDEABLE.filter((i) => i.defaultHidden).map((i) => i.key);

export function readHiddenSidebar() {
  try {
    const stored = localStorage.getItem('asa_sidebar_hidden');
    if (stored === null) return new Set(SIDEBAR_HIDEABLE_DEFAULTS);
    return new Set(JSON.parse(stored));
  } catch (_e) { return new Set(SIDEBAR_HIDEABLE_DEFAULTS); }
}

export function writeHiddenSidebar(hiddenSet) {
  try {
    localStorage.setItem('asa_sidebar_hidden', JSON.stringify(Array.from(hiddenSet)));
    window.dispatchEvent(new CustomEvent('asa-sidebar-prefs-updated'));
  } catch (_e) { /* noop */ }
}

// Iter 62cv — Sidebar full customization (Rayan msg 2026-02-20 : reorder + custom shortcuts).
// Founder-only. Stored in localStorage.
export function readSidebarOrder() {
  try {
    const raw = localStorage.getItem('asa_sidebar_order');
    return raw ? JSON.parse(raw) : [];
  } catch (_e) { return []; }
}
export function writeSidebarOrder(order) {
  try {
    localStorage.setItem('asa_sidebar_order', JSON.stringify(order || []));
    window.dispatchEvent(new CustomEvent('asa-sidebar-prefs-updated'));
  } catch (_e) { /* noop */ }
}
export function readSidebarCustoms() {
  try {
    const raw = localStorage.getItem('asa_sidebar_customs');
    return raw ? JSON.parse(raw) : [];
  } catch (_e) { return []; }
}
export function writeSidebarCustoms(customs) {
  try {
    localStorage.setItem('asa_sidebar_customs', JSON.stringify(customs || []));
    window.dispatchEvent(new CustomEvent('asa-sidebar-prefs-updated'));
  } catch (_e) { /* noop */ }
}

// Returns the full built-in items list filtered by role, with a stable `_id` per row
// (`to` when present, `key` for external, or the label as last resort). Used both by
// the sidebar renderer AND the Settings "Éditables" list to keep them in perfect sync.
export function getAllSidebarItems(user) {
  const combine = (arr, section) => arr
    .filter((it) => it.roles.includes(user?.role) || (it.extraUserIds || []).includes(user?.id))
    .map((it) => ({ ...it, _id: it.to || it.key || it.label, _section: section }));
  return [
    ...combine(PRINCIPAL, 'Principal'),
    ...combine(PLANNING, 'Planning'),
    ...combine(WORKSPACE, 'Espace de travail'),
    ...combine(SETTINGS, 'Paramètres'),
  ];
}

const PRINCIPAL = [
  { to: '/',                label: 'Overview',        icon: Home,          roles: ['founder', 'gestionnaire', 'contributor', 'client'] },
  { to: '/messages',        label: 'Messagerie',      icon: MessageCircle, roles: ['founder', 'gestionnaire', 'contributor'] },
  // Iter 62ac — Écosystème Elevate Influence : liens externes vers les 2 SaaS frères (founders STRICT only, hidden for gestionnaire)
  { external: true, key: 'sales-elevate', label: 'Sales Elevate',   href: 'https://studio-ops-dash.emergent.host',   logo: '/elevate-logo.png', roles: ['founder'] },
  // Iter 62bc — Elevate School devient une page native ASA (plus d'onglet externe).
  { to: '/elevate-school', key: 'elevate-school', label: 'Elevate School', logo: '/elevate-logo.png', roles: ['founder'] },
];

const PLANNING = [
  // Iter 62fh — Les comptes invités ('guest') ont accès UNIQUEMENT à /tasks.
  { to: '/tasks',           label: 'Tâches',          icon: CheckSquare,   roles: ['founder', 'gestionnaire', 'contributor', 'client', 'guest'] },
  { to: '/client-roadmap',  key: 'client-roadmap', label: 'Roadmap client',  icon: Target,        roles: ['founder', 'gestionnaire', 'client'] },
  { to: '/objectives',      key: 'objectives', label: 'Objectifs',       icon: Target,        roles: ['founder', 'gestionnaire'] },
  { to: '/tracking',        label: 'Tracking',        icon: TrendingUp,    roles: ['founder', 'gestionnaire', 'client'] },
];

const WORKSPACE = [
  // Iter 62ea — Notes fusionné dans Bibliothèque (module unifié).
  { to: '/clients',    key: 'clients', label: 'Clients',      icon: Users,      roles: ['founder', 'gestionnaire'] },
  { to: '/library',    label: 'Bibliothèque', icon: Folder,     roles: ['founder', 'gestionnaire', 'contributor', 'client'] },
  // Iter 62bp — ASA Script reserved to true founders (hidden for gestionnaire).
  { to: '/asa-script', key: 'asa-script', label: 'ASA Script',   icon: Sparkles,   roles: ['founder'] },
  { to: '/projects',   label: 'Projets',      icon: LayoutGrid, roles: ['founder', 'gestionnaire', 'contributor'] },
];

const SETTINGS = [
  { to: '/team',       label: 'Équipe',       icon: Users,      roles: ['founder', 'gestionnaire'] },
  { to: '/archives',   label: 'Archives',     icon: Archive,    roles: ['founder', 'gestionnaire'] },
  // Iter 62ch — Personal settings hub (Calendly, widget tuto, futurs toggles).
  { to: '/reglages',   label: 'Réglages',     icon: SettingsIcon, roles: ['founder', 'gestionnaire', 'contributor', 'client', 'editor'] },
];

export function AppLayout({ children }) {
  const { user, logout, isImpersonating, stopImpersonate } = useAuth();
  const isMobile = useIsMobile();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [verif, setVerif] = useState(null);
  // Iter40 — dismissable verif banner (persisted per day in localStorage)
  const [verifDismissed, setVerifDismissed] = useState(() => {
    const stamp = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
    return localStorage.getItem('asa_verif_dismissed') === stamp;
  });
  const dismissVerif = () => {
    const stamp = new Date().toISOString().slice(0, 10);
    localStorage.setItem('asa_verif_dismissed', stamp);
    setVerifDismissed(true);
  };
  const loc = useLocation();
  const isOverview = loc.pathname === '/';

  // Close mobile sidebar on route change
  useEffect(() => { setMobileOpen(false); }, [loc.pathname]);

  // Iter 62cu — Live-read of user sidebar preferences (localStorage). Reacts to
  // toggles from the Réglages page via the 'asa-sidebar-prefs-updated' event.
  const [hiddenSet, setHiddenSet] = useState(() => readHiddenSidebar());
  const [customOrder, setCustomOrder] = useState(() => readSidebarOrder());
  const [customShortcuts, setCustomShortcuts] = useState(() => readSidebarCustoms());
  useEffect(() => {
    const handler = () => {
      setHiddenSet(readHiddenSidebar());
      setCustomOrder(readSidebarOrder());
      setCustomShortcuts(readSidebarCustoms());
    };
    window.addEventListener('asa-sidebar-prefs-updated', handler);
    window.addEventListener('storage', handler);
    return () => {
      window.removeEventListener('asa-sidebar-prefs-updated', handler);
      window.removeEventListener('storage', handler);
    };
  }, []);
  const filterHidden = (items) => items.filter((it) => !(it.key && hiddenSet.has(it.key)));

  // Poll verification slot every minute
  useEffect(() => {
    const fetchSlot = async () => {
      try { const r = await api.get('/notifications/verification'); setVerif(r.data); } catch (e) { /* ignore */ }
    };
    fetchSlot();
    const id = setInterval(fetchSlot, 60000);
    return () => clearInterval(id);
  }, []);

  // Iter47r — Founder-only pending signups notification (big blocking modal + badge)
  const isFounder = user?.role === 'founder';
  const [pending, setPending] = useState([]);
  const [pendingModalOpen, setPendingModalOpen] = useState(false);
  const autoOpenedRef = useRef(false);
  const loadPending = () => {
    if (!isFounder) return;
    api.get('/users/pending').then((r) => setPending(r.data || [])).catch(() => {});
  };
  useEffect(() => {
    if (!isFounder) return;
    loadPending();
    const id = setInterval(loadPending, 45000); // refresh every 45s
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFounder]);
  // Auto-open on first arrival of pending users (once per browser session)
  useEffect(() => {
    if (!isFounder || pending.length === 0) return;
    const sessionKey = 'asa_pending_shown';
    if (autoOpenedRef.current) return;
    if (sessionStorage.getItem(sessionKey) === 'yes') return;
    setPendingModalOpen(true);
    sessionStorage.setItem(sessionKey, 'yes');
    autoOpenedRef.current = true;
  }, [pending, isFounder]);

  return (
    <div style={{ minHeight: '100dvh', background: 'var(--bg)' }} className={isOverview ? 'route-overview' : ''}>
      {/* Iter 62cn — Impersonation banner: shown at the top when a founder swapped
          to another user's account via TeamPage → "Voir sa vue". Sticky, one click to restore. */}
      {isImpersonating && (
        <div
          data-testid="impersonate-banner"
          style={{
            position: 'sticky', top: 0, zIndex: 200,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
            padding: '10px 18px',
            background: 'linear-gradient(90deg, rgba(30,95,255,0.35) 0%, rgba(91,190,252,0.30) 50%, rgba(30,95,255,0.35) 100%)',
            borderBottom: '1.5px solid rgba(91,190,252,0.55)',
            backdropFilter: 'blur(10px)',
            fontFamily: 'Sora',
          }}
        >
          <span style={{ fontSize: 13, fontWeight: 700, color: '#FFFFFF', letterSpacing: 0.2 }}>
            Tu vois ASA depuis le compte de <strong>{user?.name || 'ce monteur'}</strong>
          </span>
          <button
            onClick={stopImpersonate}
            data-testid="stop-impersonate-btn"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              padding: '6px 14px', borderRadius: 8,
              background: '#FFFFFF', border: '1.5px solid rgba(255,255,255,0.6)',
              color: '#1E5FFF', cursor: 'pointer',
              fontFamily: 'Sora', fontSize: 12.5, fontWeight: 800,
              boxShadow: '0 4px 12px rgba(30,95,255,0.35)',
            }}
          >
            ← Retour à mon compte
          </button>
        </div>
      )}
      {/* Iter61c — Sidebar completely hidden on mobile (msg 757). Bottom nav + Profil handle navigation. */}
      {!isMobile && (
      <aside className={`asa-sidebar ${mobileOpen ? 'mobile-open' : ''} ${isOverview ? 'overview-drawer' : ''}`}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '20px 18px 14px' }}>
          <Logo size={30} />
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: '1.02rem', color: 'var(--t1)', letterSpacing: '-0.02em' }}>ASA</div>
            <div className="asa-eyebrow" style={{ fontSize: '0.58rem' }}>by Elevate Influence</div>
          </div>
          <button
            className="mobile-close"
            onClick={() => setMobileOpen(false)}
            style={{ padding: 6, borderRadius: 6 }}
          >
            <X size={16} color="var(--t3)" />
          </button>
        </div>

        <nav style={{ flex: 1, padding: '4px 12px', overflowY: 'auto' }}>
          {(() => {
            // Iter 62cv — If founder has set a custom order OR added shortcuts,
            // render ONE flat "Ma barre" section in their order. Otherwise
            // fall back to the default 4-section layout.
            const hasCustom = (customOrder && customOrder.length > 0) || (customShortcuts && customShortcuts.length > 0);
            if (hasCustom) {
              const allBuiltins = getAllSidebarItems(user);
              const builtinById = new Map(allBuiltins.map((it) => [it._id, it]));
              const customById = new Map((customShortcuts || []).map((c) => [`custom:${c.id}`, {
                _id: `custom:${c.id}`,
                external: true,
                href: c.url,
                label: c.label,
                emoji: c.emoji || '🔗',
                _custom: true,
              }]));
              const seen = new Set();
              const orderedItems = [];
              // 1) Items explicitly in the custom order (deduped)
              for (const id of (customOrder || [])) {
                if (seen.has(id)) continue;
                seen.add(id);
                if (builtinById.has(id)) orderedItems.push(builtinById.get(id));
                else if (customById.has(id)) orderedItems.push(customById.get(id));
              }
              // 2) Append any remaining builtins (respecting hidden set)
              for (const it of allBuiltins) {
                if (!seen.has(it._id)) orderedItems.push(it);
              }
              // 3) Append any custom shortcuts not yet placed
              for (const [id, c] of customById.entries()) {
                if (!seen.has(id)) orderedItems.push(c);
              }
              const filtered = orderedItems.filter((it) => !(it.key && hiddenSet.has(it.key)));
              return (
                <>
                  <SidebarSection label="Ma barre" items={filtered} extraBadges={isFounder && pending.length > 0 ? { '/team': pending.length } : undefined} />
                  <VerifChip verif={verif} />
                </>
              );
            }
            // Default : 4 sections comme avant
            return (
              <>
                <SidebarSection label="Principal"          items={filterHidden(PRINCIPAL.filter((it) => it.roles.includes(user?.role)))} />
                <SidebarSection label="Planning"           items={filterHidden(PLANNING.filter((it) => it.roles.includes(user?.role)))} />
                <SidebarSection label="Espace de travail"  items={filterHidden(WORKSPACE.filter((it) => it.roles.includes(user?.role) || (it.extraUserIds || []).includes(user?.id)))} />
                <SidebarSection label="Paramètres"         items={SETTINGS.filter((it) => it.roles.includes(user?.role))} extraBadges={isFounder && pending.length > 0 ? { '/team': pending.length } : undefined} />
                <VerifChip verif={verif} />
              </>
            );
          })()}
        </nav>

        {/* Iter47r — Pending signups pill (founder only, one-click to reopen modal) */}
        {isFounder && pending.length > 0 && (
          <button
            data-testid="pending-signups-pill"
            onClick={() => setPendingModalOpen(true)}
            style={{
              margin: '0 12px 8px', padding: '9px 12px',
              display: 'flex', alignItems: 'center', gap: 9,
              width: 'calc(100% - 24px)',
              background: 'linear-gradient(135deg, rgba(255,107,107,0.18), rgba(255,107,107,0.08))',
              border: '1.5px solid rgba(255,107,107,0.55)',
              borderRadius: 11,
              color: '#FF6B6B',
              fontFamily: 'Sora', fontWeight: 700, fontSize: 11.5,
              cursor: 'pointer', textAlign: 'left',
              animation: 'pendingPulse 2.4s ease-in-out infinite',
            }}
          >
            <span style={{
              width: 22, height: 22, borderRadius: 999,
              background: '#FF6B6B', color: '#fff',
              display: 'grid', placeItems: 'center', flexShrink: 0,
              fontFamily: 'Sora', fontWeight: 800, fontSize: 11,
            }}>{pending.length}</span>
            <span style={{ flex: 1 }}>
              {pending.length === 1 ? 'Demande en attente' : 'Demandes en attente'}
            </span>
          </button>
        )}
        <style>{`
          @keyframes pendingPulse {
            0%, 100% { box-shadow: 0 0 0 0 rgba(255,107,107,0.4); }
            50% { box-shadow: 0 0 0 6px rgba(255,107,107,0); }
          }
        `}</style>

        <div style={{
          margin: 12, padding: 11, borderRadius: 13,
          background: 'var(--sf2)', border: '1.5px solid var(--bd)',
          display: 'flex', alignItems: 'center', gap: 9,
        }}>
          <Avatar user={user} size={32} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: '0.78rem', color: 'var(--t1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {user?.name}
            </div>
            <div style={{ fontFamily: 'Lexend', fontSize: '0.64rem', color: 'var(--t3)' }}>
              {user?.role === 'founder' ? 'Founder' : user?.role === 'gestionnaire' ? 'Gestionnaire' : user?.role === 'client' ? 'Client' : 'Editor'}
            </div>
          </div>
          {/* Iter47 — Notification bell moved to sidebar footer, panel opens to the right */}
          <NotifBell placement="sidebar" />
          <button
            data-testid="sidebar-logout"
            onClick={() => {
              // Direct logout — no confirm (was blocking on some browsers)
              localStorage.removeItem('asa_token');
              // Iter 62ee — clear session-scoped animation counters (Bibliothèque)
              try {
                Object.keys(sessionStorage).forEach((k) => {
                  if (k.startsWith('libAnim_')) sessionStorage.removeItem(k);
                });
              } catch {}
              window.location.replace('/login');
            }}
            style={{ padding: 6, borderRadius: 7 }}
            title="Déconnexion"
          >
            <LogOut size={13} color="var(--t3)" />
          </button>
        </div>
      </aside>
      )}

      {/* Mobile backdrop (also used for overview drawer) */}
      {mobileOpen && !isMobile && (
        <div
          onClick={() => setMobileOpen(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 35,
            background: 'rgba(6, 11, 24, 0.55)', backdropFilter: 'blur(2px)',
          }}
        />
      )}

      {/* Main */}
      <main className={`asa-main ${isOverview ? 'main-fullscreen' : ''}`}>
        {/* Iter47e — Floating menu + notif on overview fullscreen (no sidebar visible) */}
        {isOverview && (
          <>
            {/* Iter47t — Hover-hotzone on the left edge that reveals the sidebar (desktop only) */}
            {!isMobile && (
              <div
                data-testid="overview-hover-hotzone"
                onMouseEnter={() => setMobileOpen(true)}
                style={{
                  position: 'fixed', top: 0, left: 0, bottom: 0,
                  width: 22, zIndex: 25,
                }}
              />
            )}

            {/* Iter61c — NotifBell hidden on mobile overview (MobileDashboardPage renders its own topbar layout).
                On desktop it's the floating top-right bell. */}
            {!isMobile && (
              <div style={{
                position: 'fixed', top: 16, right: 18, zIndex: 30,
                display: 'flex', gap: 8, alignItems: 'center',
              }}>
                <NotifBell placement="mobile" />
              </div>
            )}

            {/* Iter47t — Big central CTA "Accéder à votre espace de travail" (top center)
                Iter61b — Hidden on mobile per user feedback (msg 756). Only shown on desktop. */}
            {!mobileOpen && !isMobile && (
              <button
                data-testid="overview-open-workspace"
                onClick={() => setMobileOpen(true)}
                style={{
                  position: 'fixed', top: 22, left: '50%', transform: 'translateX(-50%)',
                  zIndex: 30,
                  display: 'inline-flex', alignItems: 'center', gap: 10,
                  padding: '12px 22px', borderRadius: 999,
                  background: 'linear-gradient(135deg, rgba(91,190,252,0.24), rgba(46,163,242,0.18))',
                  border: '1.5px solid rgba(91,190,252,0.55)',
                  color: '#E6EDF7',
                  fontFamily: 'Sora', fontWeight: 700, fontSize: 13,
                  letterSpacing: '-0.005em',
                  cursor: 'pointer',
                  boxShadow: '0 12px 32px rgba(46,163,242,0.28)',
                  backdropFilter: 'blur(10px)',
                  transition: 'transform 0.15s var(--ease-out), box-shadow 0.15s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateX(-50%) translateY(2px)'; e.currentTarget.style.boxShadow = '0 16px 40px rgba(46,163,242,0.42)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateX(-50%)'; e.currentTarget.style.boxShadow = '0 12px 32px rgba(46,163,242,0.28)'; }}
              >
                <LayoutGrid size={14} strokeWidth={1.8} />
                Accéder à votre espace de travail
              </button>
            )}
          </>
        )}
        {/* Mobile top bar — Iter61e: never rendered on mobile (new iOS design has its own topbar per page).
            Kept for desktop <= 900px tablet mode only. */}
        {!isOverview && !isMobile && (
        <div className="asa-mobile-topbar">
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileOpen(true)}
            style={{ padding: 8, borderRadius: 9, background: 'var(--sf)', border: '1.5px solid var(--bd)' }}
          >
            <Menu size={16} color="var(--t1)" />
          </button>
          <div style={{ flex: 1 }} />
          <NotifBell placement="mobile" />
        </div>
        )}

        {verif?.active && !verifDismissed && <VerificationBanner verif={verif} onDismiss={dismissVerif} isOverview={isOverview} />}

        {children}
      </main>

      <style>{`
        .asa-sidebar {
          position: fixed; left: 0; top: 0; bottom: 0;
          width: 244px;
          background: linear-gradient(180deg, rgba(20,29,66,0.95) 0%, rgba(11,19,48,0.95) 100%);
          backdrop-filter: blur(24px) saturate(180%);
          -webkit-backdrop-filter: blur(24px) saturate(180%);
          border-right: 1px solid rgba(255,255,255,0.06);
          display: flex;
          flex-direction: column;
          z-index: 40;
          transition: transform 0.3s var(--ease-out);
          box-shadow: 0 0 40px rgba(0,0,0,0.4);
        }
        .asa-sidebar::before {
          content: '';
          position: absolute; inset: 0;
          background-image: url('/asa-logo.png');
          background-size: 180px;
          background-repeat: no-repeat;
          background-position: center 65%;
          opacity: 0.04;
          pointer-events: none;
          filter: drop-shadow(0 0 40px rgba(62,123,250,0.5));
        }
        .asa-sidebar > * { position: relative; z-index: 1; }
        body.note-editor-open .asa-sidebar { display: none !important; }
        body.note-editor-open .asa-main { padding-left: 0 !important; }
        .asa-main {
          min-height: 100vh;
          min-height: 100dvh;
          padding-left: 244px;
        }
        .asa-main.main-fullscreen { padding-left: 0 !important; }
        .asa-sidebar.overview-drawer { transform: translateX(-100%); }
        .asa-sidebar.overview-drawer.mobile-open { transform: translateX(0); }
        .asa-mobile-topbar { display: none; }
        .mobile-close { display: none; }

        @media (max-width: 900px) {
          .asa-sidebar { transform: translateX(-100%); }
          .asa-sidebar.mobile-open { transform: translateX(0); }
          .asa-main { padding-left: 0; }
          .asa-mobile-topbar {
            display: flex; align-items: center; gap: 10px;
            padding: 12px 16px;
            position: sticky; top: 0; z-index: 20;
            background: rgba(11, 19, 48, 0.85);
            backdrop-filter: blur(14px);
            border-bottom: 1px solid rgba(255,255,255,0.06);
          }
          .mobile-close { display: grid; place-items: center; }
        }
        @media (max-width: 640px) {
          /* Iter61n — Room for the iOS bottom nav (58px + safe-area). */
          .asa-main { padding-bottom: calc(env(safe-area-inset-bottom, 0px) + 66px); }
          /* Hide the hamburger button — the bottom nav is now the primary navigation */
          .asa-mobile-topbar [data-testid="mobile-menu-toggle"] { display: none; }
        }
        @media (min-width: 901px) { .mobile-close { display: none; } }
      `}</style>

      {/* Iter47r — Big blocking notification when signups are waiting for approval */}
      {isFounder && pendingModalOpen && pending.length > 0 && (
        <PendingSignupsModal
          pending={pending}
          onClose={() => setPendingModalOpen(false)}
          onDone={loadPending}
        />
      )}

      {/* Iter61n — Mobile bottom nav (iOS style) — replaces hamburger on mobile */}
      {isMobile && user && <MobileBottomNav />}
    </div>
  );
}

function SidebarSection({ label, items, extraBadges }) {
  const loc = useLocation();
  const [badges, setBadges] = useState({});
  // Iter 62ax — Elevate Influence sidebar badge (external link).
  const elevateNotifs = useElevateNotifCount();
  // Iter47l — collapsible sections (chevron), persisted per label
  const storageKey = `asa_sidebar_collapsed_${label}`;
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(storageKey) === '1');
  useEffect(() => { localStorage.setItem(storageKey, collapsed ? '1' : '0'); }, [collapsed, storageKey]);

  useEffect(() => {
    if (!items.some((it) => it.to === '/notes')) return;
    let cancelled = false;
    const fetchBadges = async () => {
      try {
        const r = await api.get('/notes/badge');
        if (!cancelled) setBadges((b) => ({ ...b, '/notes': r.data.count || 0 }));
      } catch (e) { /* ignore */ }
    };
    fetchBadges();
    const id = setInterval(fetchBadges, 30000);
    return () => { cancelled = true; clearInterval(id); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length]);

  // Merge external badges (e.g. pending signups from AppLayout)
  const allBadges = useMemo(() => ({ ...badges, ...(extraBadges || {}) }), [badges, extraBadges]);

  if (!items || items.length === 0) return null;

  // If any child is active, force-open the section
  const anyActive = items.some((it) => loc.pathname === it.to || (it.to !== '/' && loc.pathname.startsWith(it.to)));
  const isCollapsed = collapsed && !anyActive;

  return (
    <div style={{ marginBottom: 16 }}>
      <button
        type="button"
        data-testid={`sidebar-toggle-${label.toLowerCase().replace(/\s+/g, '-')}`}
        onClick={() => setCollapsed((c) => !c)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          width: '100%', padding: '7px 10px',
          background: 'transparent', border: 'none',
          cursor: 'pointer', textAlign: 'left',
        }}
      >
        <ChevronDown
          size={11}
          color="var(--t3)"
          strokeWidth={2}
          style={{
            transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
            transition: 'transform 0.15s var(--ease-out)',
          }}
        />
        <span className="asa-eyebrow" style={{ fontSize: '0.58rem', padding: 0, flex: 1 }}>{label}</span>
      </button>
      {!isCollapsed && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {items.map((it) => {
          // Iter 62ac — External SaaS links (Sales Elevate / Elevate School)
          // Iter 62cv — Aussi utilisé pour les raccourcis custom (avec `it.emoji` au lieu d'un logo image).
          if (it.external) {
            return (
              <a
                key={it.key || it._id}
                href={it.href}
                target="_blank"
                rel="noreferrer noopener"
                data-testid={`nav-external-${it.key || it._id}`}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '8px 10px', borderRadius: 9,
                  background: 'transparent',
                  border: '1px solid transparent',
                  color: 'var(--t2)',
                  fontFamily: 'Sora', fontWeight: 600, fontSize: '0.8rem',
                  textDecoration: 'none', transition: 'all 0.15s var(--ease-out)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(91,190,252,0.08)';
                  e.currentTarget.style.borderColor = 'rgba(91,190,252,0.25)';
                  e.currentTarget.style.color = 'var(--t1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.borderColor = 'transparent';
                  e.currentTarget.style.color = 'var(--t2)';
                }}
              >
                {it.logo ? (
                  <img
                    src={it.logo}
                    alt=""
                    width={18}
                    height={18}
                    style={{
                      borderRadius: 5,
                      objectFit: 'cover',
                      boxShadow: '0 1px 4px rgba(46,163,242,0.35)',
                      flexShrink: 0,
                    }}
                  />
                ) : (
                  <span
                    aria-hidden
                    style={{
                      width: 18, height: 18, borderRadius: 5,
                      display: 'grid', placeItems: 'center',
                      background: 'rgba(245,197,106,0.15)',
                      border: '1px solid rgba(245,197,106,0.30)',
                      fontSize: 11, flexShrink: 0,
                    }}
                  >{it.emoji || '🔗'}</span>
                )}
                <span style={{ flex: 1 }}>{it.label}</span>
                {/* Iter 62ax — Live notif count for Elevate Influence */}
                {it.key === 'elevate-school' && elevateNotifs > 0 && (
                  <span
                    data-testid="nav-elevate-badge"
                    style={{
                      padding: '1px 7px', borderRadius: 999,
                      background: '#FF6B6B', color: '#fff',
                      fontFamily: 'Sora', fontWeight: 800, fontSize: '0.6rem',
                      letterSpacing: '0.02em',
                    }}
                  >{elevateNotifs}</span>
                )}
                <span
                  aria-hidden
                  style={{
                    fontFamily: 'Sora', fontWeight: 700, fontSize: 9,
                    color: 'var(--t4)', letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                  }}
                >↗</span>
              </a>
            );
          }
          const Icon = it.icon;
          const active = loc.pathname === it.to || (it.to !== '/' && loc.pathname.startsWith(it.to));
          const badge = allBadges[it.to];
          return (
            <NavLink
              key={it.to}
              to={it.to}
              data-testid={`nav-${it.to.replace('/', '') || 'home'}`}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '8px 10px', borderRadius: 9,
                background: active ? 'var(--pbg)' : 'transparent',
                border: `1px solid ${active ? 'var(--pbd)' : 'transparent'}`,
                color: active ? 'var(--pl)' : 'var(--t2)',
                fontFamily: 'Sora', fontWeight: 600, fontSize: '0.8rem',
                textDecoration: 'none', transition: 'all 0.15s var(--ease-out)',
              }}
            >
              {it.logo ? (
                <img
                  src={it.logo}
                  alt=""
                  width={16}
                  height={16}
                  style={{
                    borderRadius: 4,
                    objectFit: 'cover',
                    boxShadow: '0 0 0 1px rgba(91,190,252,0.35), 0 4px 10px -4px rgba(91,190,252,0.55)',
                  }}
                />
              ) : Icon ? (
                <Icon size={14} strokeWidth={active ? 2 : 1.6} />
              ) : null}
              <span style={{ flex: 1 }}>{it.label}</span>
              {badge > 0 && (
                <span data-testid={`badge${it.to}`} style={{
                  padding: '1px 7px', borderRadius: 999,
                  background: '#FF6B6B', color: '#fff',
                  fontFamily: 'Sora', fontWeight: 800, fontSize: '0.6rem',
                  letterSpacing: '0.02em',
                }}>+{badge}</span>
              )}
            </NavLink>
          );
        })}
        </div>
      )}
    </div>
  );
}

// Sidebar countdown chip — visible at all times, highlights when slot is active
function VerifChip({ verif }) {
  const [now, setNow] = useState(new Date());
  useEffect(() => { const id = setInterval(() => setNow(new Date()), 30000); return () => clearInterval(id); }, []);

  // Paris hour (approx UTC+2 — matches backend behaviour)
  const parisH = (now.getUTCHours() + 2) % 24;
  const parisM = now.getUTCMinutes();

  const active = !!verif?.active;
  // Compute "in Xh Ym" until next 17h
  let countdown = '';
  if (!active) {
    let hoursTo = 17 - parisH;
    let minsTo = -parisM;
    if (minsTo < 0) { hoursTo -= 1; minsTo += 60; }
    if (hoursTo < 0) hoursTo += 24;
    countdown = hoursTo > 0 ? `dans ${hoursTo}h${String(minsTo).padStart(2, '0')}` : `dans ${minsTo}min`;
  } else {
    countdown = verif.label;
  }

  const color = active ? (verif.color || '#FF6B6B') : '#5BBEFC';
  const bg = active ? `${color}22` : 'var(--sf2)';
  const border = active ? `${color}55` : 'var(--bd)';

  return (
    <div style={{ marginTop: 14, padding: '0 4px' }}>
      <div className="asa-eyebrow" style={{ padding: '0 6px 6px', fontSize: '0.58rem' }}>
        Rituel ASA
      </div>
      <div
        data-testid="verif-chip"
        style={{
          padding: '10px 12px', borderRadius: 11,
          background: bg, border: `1.5px solid ${border}`,
          display: 'flex', alignItems: 'center', gap: 10,
          transition: 'all 0.25s var(--ease-out)',
        }}
      >
        <div style={{
          width: 28, height: 28, borderRadius: 8,
          background: `${color}26`, border: `1.5px solid ${color}55`,
          display: 'grid', placeItems: 'center', flexShrink: 0,
          animation: active ? 'pulseRing 2s ease-in-out infinite' : 'none',
        }}>
          <AlertTriangle size={13} color={color} strokeWidth={2} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontFamily: 'Sora', fontWeight: 800, fontSize: '0.7rem',
            color, letterSpacing: '0.04em', textTransform: 'uppercase',
          }}>
            {active ? 'En cours' : 'Vérif 17h–19h'}
          </div>
          <div style={{ fontFamily: 'Lexend', fontSize: '0.68rem', color: 'var(--t2)', marginTop: 1 }}>
            {countdown}
          </div>
        </div>
      </div>
      <style>{`
        @keyframes pulseRing {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.08); }
        }
      `}</style>
    </div>
  );
}

// Verification banner shown during the slot (17h-19h Paris time)
// Iter40: compact one-line layout + dismiss button
// Iter47i: z-index above floating overview icons + right-padding so bell+menu don't cover the text
function VerificationBanner({ verif, onDismiss, isOverview }) {
  return (
    <div
      className="verif-banner"
      data-testid="verif-banner"
      style={{
        position: 'sticky', top: 0, zIndex: 45,
        background: `${verif.color}1F`,
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        borderBottom: `1.5px solid ${verif.color}44`,
        padding: isOverview ? '8px 110px 8px 22px' : '8px 22px',
        display: 'flex', alignItems: 'center', gap: 10,
      }}
    >
      <AlertTriangle size={14} color={verif.color} strokeWidth={2} style={{ flexShrink: 0 }} />
      <div style={{
        fontFamily: 'Sora', fontWeight: 700, fontSize: '0.78rem',
        color: verif.color, letterSpacing: '0.02em', flexShrink: 0,
      }}>
        {verif.label}
      </div>
      <div style={{
        fontFamily: 'Lexend', fontSize: '0.74rem', color: 'var(--t2)',
        flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {verif.message}
      </div>
      <button
        data-testid="verif-banner-dismiss"
        onClick={onDismiss}
        title="Masquer pour aujourd'hui"
        style={{
          padding: 4, borderRadius: 6, background: 'transparent',
          border: 'none', cursor: 'pointer', display: 'flex',
          alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}
      >
        <X size={14} color={verif.color} />
      </button>
    </div>
  );
}

// Notification bell with dropdown panel — polls /api/notifications and shows in-app alerts
export function NotifBell({ placement = 'mobile' }) {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const [r1, r2] = await Promise.all([
        api.get('/notifications?limit=30'),
        api.get('/notifications/unread-count'),
      ]);
      setItems(r1.data || []);
      setUnread(r2.data?.count || 0);
    } catch (e) { /* ignore poll errors */ }
  };
  useEffect(() => {
    load();
    const id = setInterval(load, 25000);  // poll every 25s
    return () => clearInterval(id);
  }, []);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (!e.target.closest('[data-notif-pop]') && !e.target.closest('[data-testid="notif-bell"]')) {
        setOpen(false);
      }
    };
    window.addEventListener('click', onDoc);
    return () => window.removeEventListener('click', onDoc);
  }, [open]);

  const markAllRead = async () => {
    try { await api.post('/notifications/mark-all-read'); await load(); }
    catch (e) { /* ignore */ }
  };

  const handleClick = async (n) => {
    if (!n.read) {
      try { await api.patch(`/notifications/${n.id}/read`); } catch (e) { /* ignore */ }
    }
    setOpen(false);

    const isFrame = (n.type || '').startsWith('frameio_');

    // Iter 62ac — External SaaS notifications (Sales Elevate / Elevate School)
    // → open the SaaS URL in a new tab, never route inside ASA.
    if (n.external || (n.type || '').startsWith('external_') || (n.url && /^https?:\/\//i.test(n.url))) {
      if (n.url) window.open(n.url, '_blank', 'noopener,noreferrer');
      load();
      return;
    }

    // Iter61q — use the persisted deep-link URL if present, else fallback to legacy inference
    if (n.url) {
      if (n.project_id && n.url.startsWith('/projects')) {
        localStorage.setItem('asa_open_project', n.project_id);
      }
      navigate(n.url);
    } else if (n.task_id) {
      // Iter62y — Open the specific task directly (auto-focus Frame section for frameio notifs)
      navigate(`/tasks?open=${encodeURIComponent(n.task_id)}${isFrame ? '&focus=frame' : ''}`);
    } else if (n.project_id) {
      localStorage.setItem('asa_open_project', n.project_id);
      navigate('/projects');
    } else if (n.note_id) {
      navigate(`/notes?note=${encodeURIComponent(n.note_id)}`);
    } else if (n.conversation_id) {
      navigate(`/messages?conv=${encodeURIComponent(n.conversation_id)}`);
    } else if (n.type === 'dm_received' && n.triggered_by) {
      navigate(`/messages?peer=${encodeURIComponent(n.triggered_by)}`);
    } else if (n.type === 'dm_received' || n.type === 'group_message') {
      navigate('/messages');
    }
    load();
  };

  const typeMeta = (t) => ({
    block_validated:     { color: '#34D399', icon: 'check' },
    task_finalized:      { color: '#A78BFA', icon: 'check' },
    task_done:           { color: '#5BBEFC', icon: 'check' },
    task_assigned:       { color: '#5BBEFC', icon: 'plus' },
    task_review:         { color: '#F5A623', icon: 'bell' },
    task_delete_request: { color: '#FF6B6B', icon: 'bell' },
    frameio_submitted:   { color: '#F5A623', icon: 'bell' },
    frameio_reviewed:    { color: '#A78BFA', icon: 'check' },
    dm_received:         { color: '#25D366', icon: 'message' },
    group_message:       { color: '#25D366', icon: 'message' },
    note_shared:         { color: '#F5A623', icon: 'note' },
    note_edited:         { color: '#F5A623', icon: 'note' },
    project_assigned:    { color: '#5BBEFC', icon: 'plus' },
    daily_bilan:         { color: '#F5A623', icon: 'check' },
    external_sales:      { color: '#5BBEFC', icon: 'bell' },
    external_school:     { color: '#5BBEFC', icon: 'bell' },
    elevate_task_created:  { color: '#5BBEFC', icon: 'plus' },
    elevate_task_due:      { color: '#F5A623', icon: 'bell' },
    elevate_task_overdue:  { color: '#FF6B6B', icon: 'bell' },
    elevate_task_done:     { color: '#34D399', icon: 'check' },
    elevate_cash_alert:    { color: '#F5C56A', icon: 'bell' },
  }[t] || { color: '#A78BFA', icon: 'bell' });

  // Iter62t — Shared notification row (used in both iOS portal and desktop fallback panels).
  const renderNotifRow = (n) => {
    const m = typeMeta(n.type);
    const isPriority = [
      'task_review', 'task_delete_request', 'task_stuck', 'block_validated', 'daily_bilan',
      'frameio_submitted',
      'elevate_task_overdue', 'elevate_cash_alert',
    ].includes(n.type);
    return (
      <button
        key={n.id}
        data-testid={`notif-item-${n.id}`}
        onClick={() => handleClick(n)}
        style={{
          display: 'flex', alignItems: 'flex-start', gap: 12,
          width: '100%',
          padding: isPriority ? '16px 16px' : '10px 14px',
          background: isPriority && !n.read
            ? `linear-gradient(135deg, ${m.color}18, ${m.color}06)`
            : n.read ? 'transparent' : 'rgba(46,163,242,0.05)',
          border: 'none',
          borderLeft: isPriority ? `3px solid ${m.color}` : 'none',
          borderBottom: '1px solid var(--bd)',
          cursor: 'pointer', textAlign: 'left', position: 'relative',
        }}
      >
        <div style={{
          width: isPriority ? 42 : 30,
          height: isPriority ? 42 : 30,
          borderRadius: isPriority ? 12 : 9,
          flexShrink: 0,
          background: isPriority
            ? `linear-gradient(135deg, ${m.color}33, ${m.color}0F)`
            : `${m.color}1A`,
          border: `1px solid ${m.color}${isPriority ? '77' : '55'}`,
          boxShadow: isPriority ? `0 6px 16px -6px ${m.color}66, inset 0 1px 0 rgba(255,255,255,0.10)` : 'none',
          display: 'grid', placeItems: 'center',
        }}>
          {m.icon === 'message' ? (
            <MessageCircle size={isPriority ? 18 : 13} color={m.color} strokeWidth={2.3} />
          ) : m.icon === 'note' ? (
            <FileText size={isPriority ? 18 : 13} color={m.color} strokeWidth={2.3} />
          ) : m.icon === 'bell' ? (
            <Bell size={isPriority ? 18 : 13} color={m.color} strokeWidth={2.3} />
          ) : m.icon === 'plus' ? (
            <Sparkles size={isPriority ? 18 : 13} color={m.color} strokeWidth={2.3} />
          ) : (
            <Check size={isPriority ? 18 : 13} color={m.color} strokeWidth={2.3} />
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          {isPriority && !n.read && (
            <div style={{
              display: 'inline-block',
              fontFamily: 'Sora', fontWeight: 800, fontSize: 8.5,
              letterSpacing: '0.08em', textTransform: 'uppercase',
              padding: '2px 7px', borderRadius: 999,
              background: `${m.color}22`, color: m.color,
              border: `1px solid ${m.color}55`,
              marginBottom: 4,
            }}>
              {n.type === 'task_review' ? 'À valider' :
               n.type === 'task_delete_request' ? 'Suppression' :
               n.type === 'task_stuck' ? 'Dépassement' :
               n.type === 'block_validated' ? 'Validée' :
               n.type === 'daily_bilan' ? 'Bilan' :
               n.type === 'elevate_task_overdue' ? 'Elevate · Retard' :
               n.type === 'elevate_cash_alert' ? 'Elevate · Cash' :
               n.type === 'frameio_submitted' ? 'Frame.io' : 'Action'}
            </div>
          )}
          <div style={{
            fontFamily: 'Sora',
            fontWeight: n.read ? 600 : 800,
            fontSize: isPriority ? 14 : 12.5,
            color: 'var(--t1)',
            overflow: 'hidden', textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: isPriority ? 2 : 1,
            WebkitBoxOrient: 'vertical',
            lineHeight: 1.3, letterSpacing: '-0.005em',
          }}>{n.title}</div>
          {n.body && (
            <div style={{
              fontFamily: 'Lexend',
              fontSize: isPriority ? 12 : 11,
              color: isPriority ? 'var(--t2)' : 'var(--t3)',
              marginTop: 3, lineHeight: 1.45,
              overflow: 'hidden', textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: isPriority ? 3 : 2, WebkitBoxOrient: 'vertical',
            }}>{n.body}</div>
          )}
          <div style={{
            fontFamily: 'Lexend', fontSize: 10, color: 'var(--t4)',
            marginTop: isPriority ? 6 : 3,
          }}>{new Date(n.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</div>
        </div>
        {!n.read && (
          <span style={{
            width: isPriority ? 10 : 7,
            height: isPriority ? 10 : 7,
            borderRadius: '50%',
            background: isPriority ? m.color : 'var(--pl)',
            marginTop: isPriority ? 14 : 13,
            flexShrink: 0,
            boxShadow: isPriority ? `0 0 10px ${m.color}88` : 'none',
          }} />
        )}
      </button>
    );
  };

  // Group by relative day
  const groups = useMemo(() => {
    const byDay = new Map();
    items.forEach((n) => {
      const d = new Date(n.created_at);
      const today = new Date();
      const yesterday = new Date(); yesterday.setDate(today.getDate() - 1);
      const isSameDay = (a, b) => a.toDateString() === b.toDateString();
      let label = d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
      if (isSameDay(d, today)) label = "Aujourd'hui";
      else if (isSameDay(d, yesterday)) label = 'Hier';
      if (!byDay.has(label)) byDay.set(label, []);
      byDay.get(label).push(n);
    });
    return Array.from(byDay.entries());
  }, [items]);

  // Panel position depends on where the bell is rendered:
  // - 'sidebar': bell is bottom-left in the sidebar footer → panel opens to the RIGHT of the sidebar (side popover, opens downward from the bell)
  // - 'mobile' : bell is in the top bar → panel opens BELOW the bell
  // - 'ios'    : Iter61c — bell inside the iOS mobile dashboard topbar; same panel layout as mobile
  const isSidebar = placement === 'sidebar';
  const isIos = placement === 'ios';
  const panelStyle = isSidebar
    ? {
        position: 'fixed', left: 246 /* sidebar width 230 + margin */, bottom: 18,
        width: 360, maxHeight: 'min(560px, 80vh)',
      }
    : isIos
    ? {
        position: 'fixed',
        top: 'env(safe-area-inset-top, 0px)',
        left: 0, right: 0, bottom: 0,
        width: 'auto', maxHeight: 'none', borderRadius: 0,
      }
    : {
        position: 'absolute', top: 'calc(100% + 8px)', right: 0,
        width: 360, maxHeight: 'min(560px, 80vh)',
      };

  return (
    <div style={{ position: 'relative' }}>
      <button
        data-testid="notif-bell"
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        title="Notifications"
        style={{
          position: 'relative',
          width: isSidebar ? 30 : isIos ? 34 : 36,
          height: isSidebar ? 30 : isIos ? 34 : 36,
          borderRadius: isSidebar ? 8 : 10,
          background: isSidebar ? 'transparent' : isIos ? '#141D42' : 'var(--sf)',
          border: isSidebar ? 'none' : isIos ? '1px solid rgba(255,255,255,0.09)' : '1.5px solid var(--bd)',
          display: 'grid', placeItems: 'center', cursor: 'pointer',
        }}
      >
        <Bell size={isSidebar ? 13 : isIos ? 15 : 15} color={isIos ? '#F5F7FB' : (unread > 0 ? 'var(--pl)' : 'var(--t3)')} strokeWidth={1.7} />
        {unread > 0 && (
          <span
            data-testid="notif-badge"
            style={{
              position: 'absolute', top: -3, right: -3,
              minWidth: 15, height: 15, padding: '0 3px',
              borderRadius: 999,
              background: 'var(--r)', border: `1.5px solid ${isIos ? '#0B1330' : 'var(--sf2)'}`,
              fontFamily: 'Sora', fontWeight: 800, fontSize: 9,
              color: '#fff', display: 'grid', placeItems: 'center',
            }}
          >{unread > 99 ? '99+' : unread}</span>
        )}
      </button>

      {open && (isIos ? createPortal(
        <>
          <div
            data-testid="notif-backdrop"
            onClick={() => setOpen(false)}
            style={{
              position: 'fixed', inset: 0, zIndex: 9998,
              background: 'rgba(6,10,20,0.72)', backdropFilter: 'blur(4px)',
            }}
          />
        <div
          data-notif-pop
          data-testid="notif-panel"
          style={{
            ...panelStyle,
            background: '#0B1330',
            border: 'none',
            borderRadius: 0,
            boxShadow: 'none',
            zIndex: 9999, overflow: 'hidden',
            display: 'flex', flexDirection: 'column',
          }}
        >
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
            padding: isIos ? '14px 18px 12px' : '12px 14px', borderBottom: '1.5px solid var(--bd)',
          }}>
            {isIos && (
              <button
                data-testid="notif-close"
                onClick={() => setOpen(false)}
                style={{
                  width: 32, height: 32, borderRadius: 10,
                  background: '#141D42', border: '1px solid rgba(255,255,255,0.09)',
                  color: '#F5F7FB', cursor: 'pointer', flexShrink: 0,
                  display: 'grid', placeItems: 'center',
                }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                  <path d="M6 6L18 18M6 18L18 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
            )}
            <div style={{ flex: 1, fontFamily: 'Sora', fontWeight: 800, fontSize: isIos ? 17 : 13.5, color: 'var(--t1)' }}>
              Notifications {unread > 0 && (
                <span style={{ color: 'var(--pl)' }}>· {unread}</span>
              )}
            </div>
            {unread > 0 && (
              <button
                data-testid="notif-mark-all-read"
                onClick={markAllRead}
                style={{
                  padding: isIos ? '7px 12px' : '5px 9px', borderRadius: isIos ? 10 : 8,
                  background: 'rgba(46,163,242,0.10)', border: '1px solid rgba(46,163,242,0.25)',
                  color: 'var(--pl)', fontFamily: 'Sora', fontWeight: 700, fontSize: isIos ? 12 : 10.5,
                  cursor: 'pointer',
                }}
              >Tout marquer lu</button>
            )}
          </div>

          {/* Iter61d — Web Push toggle at top of the notifications dropdown */}
          <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--bd)' }}>
            <PushToggle compact />
          </div>

          <div style={{ flex: 1, overflowY: 'auto' }}>
            {items.length === 0 ? (
              <div style={{
                padding: '40px 20px', textAlign: 'center',
                fontFamily: 'Lexend', fontSize: 12, color: 'var(--t3)',
              }}>
                <Bell size={22} color="var(--t4)" style={{ marginBottom: 6, opacity: 0.5 }} />
                <div>Aucune notification pour l&apos;instant</div>
              </div>
            ) : (
              groups.map(([label, list]) => (
                <div key={label}>
                  <div style={{
                    padding: '10px 14px 4px',
                    fontFamily: 'Sora', fontWeight: 700, fontSize: 10,
                    color: 'var(--t3)', letterSpacing: '0.07em', textTransform: 'uppercase',
                  }}>{label}</div>
                  {list.map((n) => renderNotifRow(n))}
                </div>
              ))
            )}
          </div>
        </div>
        </>,
        document.body
      ) : (
        <div
          data-notif-pop
          data-testid="notif-panel"
          style={{
            ...panelStyle,
            background: 'var(--sf)',
            border: '1.5px solid var(--bd)',
            borderRadius: 16,
            boxShadow: '0 30px 60px -12px rgba(0,0,0,0.75), 0 0 0 1px rgba(255,255,255,0.04)',
            zIndex: 60, overflow: 'hidden',
            display: 'flex', flexDirection: 'column',
          }}
        >
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '12px 14px', borderBottom: '1.5px solid var(--bd)',
          }}>
            <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 13.5, color: 'var(--t1)' }}>
              Notifications {unread > 0 && (
                <span style={{ color: 'var(--pl)' }}>· {unread}</span>
              )}
            </div>
            {unread > 0 && (
              <button
                onClick={markAllRead}
                style={{
                  padding: '5px 9px', borderRadius: 8,
                  background: 'rgba(46,163,242,0.10)', border: '1px solid rgba(46,163,242,0.25)',
                  color: 'var(--pl)', fontFamily: 'Sora', fontWeight: 700, fontSize: 10.5,
                  cursor: 'pointer',
                }}
              >Tout marquer lu</button>
            )}
          </div>
          <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--bd)' }}>
            <PushToggle compact />
          </div>
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {items.length === 0 ? (
              <div style={{ padding: '40px 20px', textAlign: 'center', fontFamily: 'Lexend', fontSize: 12, color: 'var(--t3)' }}>
                Aucune notification
              </div>
            ) : groups.map(([label, list]) => (
              <div key={label}>
                <div style={{ padding: '10px 14px 4px', fontFamily: 'Sora', fontWeight: 700, fontSize: 10, color: 'var(--t3)', letterSpacing: '0.07em', textTransform: 'uppercase' }}>{label}</div>
                {list.map((n) => renderNotifRow(n))}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// Reusable PageHeader (kept for back-compat with existing pages)
export function PageHeader({ title, subtitle, count, right, sticky = false }) {
  return (
    <div
      data-testid="page-header"
      className="asa-page-header"
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 10,
        padding: '20px 22px 14px',
        position: sticky ? 'sticky' : 'static', top: 0, zIndex: 10,
        background: 'var(--bg)',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <h1 className="asa-display" style={{ fontSize: 'var(--text-2xl)', margin: 0, minWidth: 0, overflowWrap: 'anywhere' }}>{title}</h1>
          {count != null && (
            <span style={{
              fontFamily: 'Sora', fontWeight: 700, fontSize: '0.72rem', color: 'var(--p)',
              padding: '3px 10px', borderRadius: 999,
              background: 'rgba(46,163,242,0.10)', border: '1px solid rgba(46,163,242,0.20)',
            }}>{count}</span>
          )}
        </div>
        {subtitle && <p className="asa-subheading" style={{ fontSize: '0.88rem', margin: '4px 0 0' }}>{subtitle}</p>}
      </div>
      <div>{right}</div>
    </div>
  );
}
